import React, { useState, useRef, useEffect } from "react";
import { Timestamp, updateDoc, doc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage, db, auth } from "../../firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";
import { updateProfile } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { userName, getPadam, getAge } from "../../lib/utils";
import { FaceDetection } from '@mediapipe/face_detection';

import {
  Spin,
  Button,
  Row,
  Col,
  Card,
  Form,
  Radio,
  Select,
  Input,
  InputNumber,
  Typography,
  DatePicker,
  TimePicker,
  Flex,
  Progress,
  message,
  Steps,
  Upload,
  Alert,
  Badge,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import jsonData from "../../data/data.json";
import {
  UserOutlined,
  BookOutlined,
  HeartOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;
const dateFormat = "MM/DD/YYYY";

// Your compressImage function
export async function compressImage(
  file,
  maxSize = 500 * 1024,
  quality = 0.7
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }

      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if too large
        const maxDimension = 1920;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64
        const compressed = canvas.toDataURL('image/jpeg', quality);
        
        if (compressed.length > maxSize) {
          reject(new Error(`Image too large: ${Math.round(compressed.length / 1024)}KB. Max: ${Math.round(maxSize / 1024)}KB`));
          return;
        }

        resolve(compressed);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Convert base64 to File object for Firebase upload
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while(n--){
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, {type:mime});
};

// MediaPipe Face Detection function (keeping same as before)
const detectFaceInImage = async (imageFile, faceDetection) => {
  return new Promise((resolve) => {
    try {
      const img = document.createElement('img');
      img.src = URL.createObjectURL(imageFile);

      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          await faceDetection.send({ image: canvas });

        } catch (error) {
          console.error('MediaPipe processing error:', error);
          resolve({
            faceDetected: false,
            confidence: 0,
            isRealPerson: false,
            faceCount: 0,
            details: {
              faceCount: 0,
              faceQuality: 'error',
              message: "Face detection processing failed. Please try again."
            }
          });
        } finally {
          URL.revokeObjectURL(img.src);
        }
      };

      img.onerror = () => {
        resolve({
          faceDetected: false,
          confidence: 0,
          isRealPerson: false,
          faceCount: 0,
          details: {
            faceCount: 0,
            faceQuality: 'error',
            message: "Unable to load image. Please try a different photo."
          }
        });
      };

      faceDetection.onResults((results) => {
        try {
          const detections = results.detections || [];
          const faceCount = detections.length;

          if (faceCount === 0) {
            resolve({
              faceDetected: false,
              confidence: 0,
              isRealPerson: false,
              faceCount: 0,
              details: {
                faceCount: 0,
                faceQuality: 'none',
                message: "No face detected. Please upload a photo with a visible face."
              }
            });
            return;
          }

          if (faceCount > 1) {
            resolve({
              faceDetected: true,
              confidence: 0,
              isRealPerson: false,
              faceCount: faceCount,
              details: {
                faceCount: faceCount,
                faceQuality: 'multiple',
                message: `${faceCount} faces detected. For matrimonial profiles, please upload a photo with only yourself.`
              }
            });
            return;
          }

          const detection = detections[0];

          let confidence = 75;
          if (detection.score !== undefined && detection.score !== null && !isNaN(detection.score)) {
            confidence = Math.round(detection.score * 100);
          }

          confidence = Math.max(0, Math.min(100, confidence));

          let faceQuality = 'good';
          let isRealPerson = true;
          let message = "Perfect! Single face verified successfully!";

          if (confidence > 70) {
            faceQuality = 'excellent';
            message = "Excellent single-person photo - verified!";
          } else if (confidence > 50) {
            faceQuality = 'good';
            message = "Good single-person photo - verified!";
          } else if (confidence > 30) {
            faceQuality = 'fair';
            message = "Single face detected - photo accepted!";
          } else {
            faceQuality = 'acceptable';
            message = "Single face detected - photo accepted!";
          }

          const bbox = detection.boundingBox;
          let faceSizeRatio = 0;

          if (bbox && bbox.width && bbox.height && img.width && img.height) {
            const faceArea = bbox.width * bbox.height;
            const imageArea = img.width * img.height;
            faceSizeRatio = faceArea / imageArea;

            if (!isNaN(faceSizeRatio)) {
              faceSizeRatio = Math.round(faceSizeRatio * 10000) / 100;
            } else {
              faceSizeRatio = 0;
            }
          }

          resolve({
            faceDetected: true,
            confidence: confidence,
            isRealPerson: true,
            faceCount: 1,
            boundingBox: bbox,
            keypoints: detection.keypoints,
            details: {
              faceCount: 1,
              faceQuality: faceQuality,
              faceSizeRatio: faceSizeRatio,
              keypointsDetected: detection.keypoints ? detection.keypoints.length : 0,
              message: message
            }
          });

        } catch (error) {
          console.error('Results processing error:', error);
          resolve({
            faceDetected: false,
            confidence: 0,
            isRealPerson: false,
            faceCount: 0,
            details: {
              faceCount: 0,
              faceQuality: 'error',
              message: "Face detection had issues. Please try again."
            }
          });
        }
      });

    } catch (error) {
      console.error('Face detection setup error:', error);
      resolve({
        faceDetected: false,
        confidence: 0,
        isRealPerson: false,
        faceCount: 0,
        details: {
          faceCount: 0,
          faceQuality: 'error',
          message: "Face detection unavailable. Please try again."
        }
      });
    }
  });
};

export default function BrahminOnboarding() {
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [user] = useAuthState(auth);
  const [progress, setProgress] = useState(0);
  const [faceDetectionReady, setFaceDetectionReady] = useState(false);
  const [loadingFaceDetection, setLoadingFaceDetection] = useState(true);
  const [faceDetection, setFaceDetection] = useState(null);
  const navigate = useNavigate();

  // Multiple photos states
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [verifyingPhotos, setVerifyingPhotos] = useState(new Set());
  const [primaryPhotoIndex, setPrimaryPhotoIndex] = useState(0);
  const maxPhotos = 6;

  // Store ALL form data across steps
  const [allFormData, setAllFormData] = useState({});

  // Initialize MediaPipe Face Detection (keeping same)
  useEffect(() => {
    const initializeFaceDetection = async () => {
      try {
        setLoadingFaceDetection(true);

        const faceDetectionInstance = new FaceDetection({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`;
          }
        });

        faceDetectionInstance.setOptions({
          model: 'short',
          minDetectionConfidence: 0.3,
        });

        await faceDetectionInstance.initialize();

        setFaceDetection(faceDetectionInstance);
        setFaceDetectionReady(true);
        message.success('Advanced face detection system ready!');

      } catch (error) {
        console.error('Error initializing MediaPipe Face Detection:', error);
        message.error('Face detection system failed to load. Please refresh the page.');
      } finally {
        setLoadingFaceDetection(false);
      }
    };

    initializeFaceDetection();

    return () => {
      if (faceDetection) {
        faceDetection.close();
      }
    };
  }, []);

  const steps = [
    {
      title: 'Photo Verification',
      icon: <SafetyOutlined />,
      description: 'Upload & verify your photos'
    },
    {
      title: 'Basic Details',
      icon: <UserOutlined />,
      description: 'Personal information'
    },
    {
      title: 'Religious Info',
      icon: <BookOutlined />,
      description: 'Brahmin community details'
    },
    {
      title: 'About You',
      icon: <HeartOutlined />,
      description: 'Tell us about yourself'
    },
  ];

  // UPDATED Photo upload function with compression
  const handleImageUpload = async (file) => {
    if (!faceDetectionReady || !faceDetection) {
      message.error('Face detection system is still loading. Please wait a moment.');
      return false;
    }

    const isValidImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
    if (!isValidImage) {
      message.error('You can only upload JPG/PNG/WEBP files!');
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < 10; // Allow up to 10MB before compression
    if (!isValidSize) {
      message.error('Image must be smaller than 10MB!');
      return false;
    }

    if (uploadedPhotos.length >= maxPhotos) {
      message.error(`You can upload maximum ${maxPhotos} photos`);
      return false;
    }

    const photoId = uuidv4();
    const preview = URL.createObjectURL(file);

    // Show compression loading
    message.loading({ content: 'Compressing image...', key: 'compress' });

    try {
      // Compress the image first
      const compressedBase64 = await compressImage(file, 500 * 1024, 0.7);
      
      // Convert compressed base64 back to File for face detection
      const compressedFile = dataURLtoFile(compressedBase64, file.name);
      
      // Calculate compression savings
      const originalSizeKB = Math.round(file.size / 1024);
      const compressedSizeKB = Math.round(compressedFile.size / 1024);
      const compressionRatio = Math.round(((originalSizeKB - compressedSizeKB) / originalSizeKB) * 100);
      
      message.success({ 
        content: `Image compressed ${compressionRatio}% (${originalSizeKB}KB → ${compressedSizeKB}KB)`, 
        key: 'compress',
        duration: 3
      });

      const newPhoto = {
        id: photoId,
        file: compressedFile, // Use compressed file for upload
        originalFile: file, // Keep reference to original
        preview,
        name: file.name,
        verified: false,
        verificationResult: null,
        isPrimary: uploadedPhotos.length === 0,
        compressed: true,
        originalSizeKB,
        compressedSizeKB,
        compressionRatio
      };

      setUploadedPhotos(prev => [...prev, newPhoto]);
      setVerifyingPhotos(prev => new Set(prev).add(photoId));

      // Run face detection on compressed file
      const result = await detectFaceInImage(compressedFile, faceDetection);

      setUploadedPhotos(prev =>
        prev.map(photo =>
          photo.id === photoId
            ? {
              ...photo,
              verified: result.faceDetected && result.isRealPerson,
              verificationResult: result
            }
            : photo
        )
      );

      if (result.faceDetected && result.isRealPerson) {
        message.success(`${result.details.message} (${result.confidence}% confidence, ${compressionRatio}% smaller)`);
      } else {
        message.error(result.details.message);
      }

    } catch (compressionError) {
      console.error('Compression error:', compressionError);
      message.error({ 
        content: 'Image compression failed. Please try a different photo.', 
        key: 'compress' 
      });
      return false;
    } finally {
      setVerifyingPhotos(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
    }

    return false;
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => {
      const filtered = prev.filter(photo => photo.id !== photoId);
      if (filtered.length > 0 && prev.find(p => p.id === photoId)?.isPrimary) {
        filtered[0].isPrimary = true;
        setPrimaryPhotoIndex(0);
      }
      return filtered;
    });
  };

  const setPrimaryPhoto = (photoId) => {
    setUploadedPhotos(prev =>
      prev.map((photo, index) => {
        const isPrimary = photo.id === photoId;
        if (isPrimary) setPrimaryPhotoIndex(index);
        return { ...photo, isPrimary };
      })
    );
  };

  const getVerifiedPhotosCount = () => {
    return uploadedPhotos.filter(photo => photo.verified).length;
  };

  const canProceedFromPhotos = () => {
    return uploadedPhotos.length > 0 && getVerifiedPhotosCount() >= 1;
  };

  // Navigation functions (keeping same logic)
  const nextStep = async () => {
    try {
      await form.validateFields();

      if (current === 0 && !canProceedFromPhotos()) {
        message.error("Please upload and verify at least one photo before proceeding.");
        return;
      }

      const currentStepValues = form.getFieldsValue();
      setAllFormData(prev => ({ ...prev, ...currentStepValues }));
      
      console.log("Saving step data:", currentStepValues);
      console.log("Total accumulated data:", { ...allFormData, ...currentStepValues });

      setCurrent(prev => Math.min(prev + 1, steps.length - 1));
    } catch (error) {
      message.error("Please fill in all required fields.");
    }
  };

  const prevStep = () => {
    const currentStepValues = form.getFieldsValue();
    setAllFormData(prev => ({ ...prev, ...currentStepValues }));
    
    setCurrent(prev => Math.max(prev - 1, 0));
  };

  // UPDATED Submit function with compressed photos
  const handleFinish = async (values) => {
    setLoading(true);

    try {
      const completeFormData = { ...allFormData, ...values };
      
      console.log("Complete form data:", completeFormData);

      const photoUrls = [];
      const verifiedPhotos = uploadedPhotos.filter(photo => photo.verified);

      // Calculate total compression savings
      const totalOriginalSize = verifiedPhotos.reduce((sum, photo) => sum + (photo.originalSizeKB || 0), 0);
      const totalCompressedSize = verifiedPhotos.reduce((sum, photo) => sum + (photo.compressedSizeKB || 0), 0);
      const totalSavings = Math.round(((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100);

      message.info(`Uploading ${verifiedPhotos.length} compressed photos (${totalSavings}% smaller)`);

      for (let i = 0; i < verifiedPhotos.length; i++) {
        const photo = verifiedPhotos[i];
        setProgress(Math.round((i / verifiedPhotos.length) * 50));

        try {
          const storageRef = ref(storage, `profiles/${user.uid}/${Date.now()}-${i}.jpg`);
          
          // Upload the compressed file (much smaller!)
          const uploadTask = uploadBytesResumable(storageRef, photo.file);

          await uploadTask;
          const url = await getDownloadURL(uploadTask.snapshot.ref);

          photoUrls.push({
            uid: photo.id || `photo-${i}`,
            url: url,
            isPrimary: photo.isPrimary === true,
            compressed: true,
            originalSizeKB: photo.originalSizeKB,
            compressedSizeKB: photo.compressedSizeKB,
            compressionRatio: photo.compressionRatio,
            verificationData: photo.verificationResult ? {
              faceDetected: photo.verificationResult.faceDetected || false,
              confidence: photo.verificationResult.confidence || 0,
              isRealPerson: photo.verificationResult.isRealPerson || false,
              faceCount: photo.verificationResult.faceCount || 0,
              details: photo.verificationResult.details || {}
            } : {
              faceDetected: false,
              confidence: 0,
              isRealPerson: false,
              faceCount: 0,
              details: {}
            },
            detectionEngine: 'MediaPipe',
          });
        } catch (uploadError) {
          console.error("Photo upload error:", uploadError);
          throw new Error("Failed to upload photos");
        }
      }

      setProgress(75);

      let dateOfBirth = null;
      let age = 0;
      
      if (completeFormData.dateOfBirth) {
        try {
          const dobDate = dayjs(completeFormData.dateOfBirth).toDate();
          dateOfBirth = Timestamp.fromDate(dobDate);
          age = getAge(completeFormData.dateOfBirth);
        } catch (error) {
          console.error("Date processing error:", error);
          dateOfBirth = null;
          age = 0;
        }
      }

      let timeOfBirth = "";
      if (completeFormData.timeOfBirth) {
        try {
          if (typeof completeFormData.timeOfBirth === 'string') {
            timeOfBirth = completeFormData.timeOfBirth;
          } else {
            timeOfBirth = dayjs(completeFormData.timeOfBirth).format("h:mm A");
          }
        } catch (error) {
          console.error("Time processing error:", error);
          timeOfBirth = "";
        }
      }

      const profileDoc = {
        profileId: uuidv4(),

        firstName: String(completeFormData.firstName || ""),
        lastName: String(completeFormData.lastName || ""),
        gender: String(completeFormData.gender || ""),
        dateOfBirth: dateOfBirth,
        maritalStatus: String(completeFormData.maritalStatus || ""),
        currentResidence: String(completeFormData.currentResidence || ""),
        motherTongue: String(completeFormData.motherTongue || ""),
        height: String(completeFormData.height || ""),
        education: String(completeFormData.education || ""),
        occupation: String(completeFormData.occupation || ""),

        subCaste: String(completeFormData.subCaste || ""),
        nakshatram: String(completeFormData.nakshatram || ""),
        padam: Number(completeFormData.padam) || 0,
        gothram: String(completeFormData.gothram || ""),
        placeOfBirth: String(completeFormData.placeOfBirth || ""),
        timeOfBirth: String(timeOfBirth),
        raasi: String(completeFormData.raasi || ""),
        dosham: String(completeFormData.dosham || ""),

        aboutSelf: String(completeFormData.aboutSelf || ""),
        partnerPreferences: String(completeFormData.partnerPreferences || ""),
        familyType: String(completeFormData.familyType || ""),

        profileImages: photoUrls,
        userId: String(user.uid),
        userName: String(userName(completeFormData.gender || "Male")),
        createdBy: String(user.displayName || `${completeFormData.firstName || ""} ${completeFormData.lastName || ""}`.trim() || "User"),
        createdAt: Timestamp.now(),
        profileCompleted: true,
        community: "Brahmin",
        isOnboardingComplete: true,
        faceVerified: true,
        faceDetectionEngine: 'MediaPipe',
        photosCompressed: true,
        totalPhotosUploaded: Number(photoUrls.length),
        verifiedPhotosCount: Number(photoUrls.length),
        photoCompressionSavings: totalSavings,
        age: Number(age),
      };

      const cleanedProfileDoc = {};
      Object.keys(profileDoc).forEach(key => {
        const value = profileDoc[key];
        if (value !== undefined) {
          if (Array.isArray(value)) {
            cleanedProfileDoc[key] = value.map(item => {
              if (typeof item === 'object' && item !== null) {
                const cleanedItem = {};
                Object.keys(item).forEach(itemKey => {
                  if (item[itemKey] !== undefined) {
                    cleanedItem[itemKey] = item[itemKey];
                  }
                });
                return cleanedItem;
              }
              return item;
            });
          } else if (typeof value === 'object' && value !== null && !value.seconds) {
            const cleanedObject = {};
            Object.keys(value).forEach(objKey => {
              if (value[objKey] !== undefined) {
                cleanedObject[objKey] = value[objKey];
              }
            });
            cleanedProfileDoc[key] = cleanedObject;
          } else {
            cleanedProfileDoc[key] = value;
          }
        }
      });

      setProgress(90);

      const undefinedCheck = JSON.stringify(cleanedProfileDoc, (key, value) => {
        if (value === undefined) {
          console.error(`Found undefined value at key: ${key}`);
          return null;
        }
        return value;
      });
      
      const finalProfileDoc = JSON.parse(undefinedCheck);

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, finalProfileDoc);

      const primaryPhoto = photoUrls.find(p => p.isPrimary) || photoUrls[0];
      if (primaryPhoto && primaryPhoto.url) {
        await updateProfile(auth.currentUser, {
          displayName: `${finalProfileDoc.firstName} ${finalProfileDoc.lastName}`.trim(),
          photoURL: primaryPhoto.url,
        });
      }

      setProgress(100);

      message.success(`Profile created successfully! Photos compressed ${totalSavings}% saving Firebase storage costs!`);
      navigate("/dashboard");

    } catch (error) {
      console.error("Profile creation error:", error);
      
      if (error.message && error.message.includes('undefined')) {
        message.error("Data validation failed. Please check all fields and try again.");
      } else {
        message.error("Failed to create profile. Please try again.");
      }
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  // Show loading screen
  if (loadingFaceDetection) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        color: 'white'
      }}>
        <Spin size="large" style={{ marginBottom: '24px' }} />
        <Title level={2} style={{ color: 'white', textAlign: 'center', marginBottom: '16px' }}>
          Initializing Face Detection
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px', textAlign: 'center' }}>
          Loading advanced AI face recognition models...
        </Text>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: '120px 24px 80px',
      position: 'relative'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        width: '300px',
        height: '300px',
        background: 'rgba(102, 126, 234, 0.1)',
        borderRadius: '50%',
        filter: 'blur(40px)'
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '5%',
        width: '200px',
        height: '200px',
        background: 'rgba(255, 215, 0, 0.15)',
        borderRadius: '50%',
        filter: 'blur(30px)'
      }}></div>

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <Title level={1} style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '16px'
          }}>
            Welcome to OkBrahmin
          </Title>
          <Paragraph style={{
            fontSize: '1.2rem',
            color: '#6b7280',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Let's create your profile in just a few steps. We'll verify your photos with AI and gather the information needed to find your perfect match.
          </Paragraph>
        </div>

        {/* Progress Steps */}
        <Card style={{
          borderRadius: '20px',
          marginBottom: '32px',
          border: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}>
          <Steps 
            current={current} 
            style={{ padding: '20px 0' }}
            size="small"
            responsive={false}
          >
            {steps.map((step, index) => (
              <Steps.Step
                key={index}
                title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{step.title}</span>}
                description={<span style={{ fontSize: '12px', color: '#6b7280' }}>{step.description}</span>}
                icon={React.cloneElement(step.icon, {
                  style: {
                    fontSize: '20px',
                    color: index <= current ? '#667eea' : '#d1d5db'
                  }
                })}
              />
            ))}
          </Steps>
        </Card>

        {/* Main Form Container */}
        <Spin spinning={loading} tip="Creating your profile...">
          <Form
            form={form}
            name="brahmin-onboarding"
            layout="vertical"
            onFinish={handleFinish}
            initialValues={allFormData}
          >
            {/* Step 1: Photo Verification */}
            {current === 0 && (
              <Card style={{
                borderRadius: '24px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {/* Card Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  padding: '40px 40px 30px',
                  margin: '-24px -24px 24px',
                  color: 'white'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <SafetyOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <Title level={3} style={{ color: 'white', margin: '0 0 16px' }}>
                      Advanced Photo Verification + Compression
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                      Upload up to {maxPhotos} photos. Each will be compressed (~80% smaller) and verified using Google's MediaPipe AI.
                    </Text>
                  </div>
                </div>

                <div style={{ padding: '0 16px' }}>
                  {uploadedPhotos.length < maxPhotos && (
                    <Row justify="center" style={{ marginBottom: '32px' }}>
                      <Col xs={24} sm={20} md={16}>
                        <Dragger
                          beforeUpload={handleImageUpload}
                          showUploadList={false}
                          accept="image/*"
                          disabled={uploadedPhotos.length >= maxPhotos || !faceDetectionReady}
                          style={{
                            borderRadius: '16px',
                            border: '2px dashed #d1d5db',
                            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                            padding: '40px 20px'
                          }}
                        >
                          <p className="ant-upload-drag-icon">
                            <PlusOutlined style={{
                              fontSize: '64px',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text'
                            }} />
                          </p>
                          <Title level={4} style={{
                            color: '#374151',
                            marginBottom: '8px'
                          }}>
                            Upload Photo {uploadedPhotos.length + 1} of {maxPhotos}
                          </Title>
                          <Paragraph style={{
                            color: '#6b7280',
                            fontSize: '16px',
                            marginBottom: '16px'
                          }}>
                            Click or drag photo to upload (JPG/PNG/WEBP, max 10MB)
                          </Paragraph>
                          <div style={{
                            background: 'rgba(34, 197, 94, 0.1)',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            display: 'inline-block',
                            marginRight: '12px'
                          }}>
                            <Text style={{ color: '#16a34a', fontWeight: '500' }}>
                              ✓ AI Ready
                            </Text>
                          </div>
                          <div style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            display: 'inline-block'
                          }}>
                            <Text style={{ color: '#2563eb', fontWeight: '500' }}>
                              ⚡ Auto-Compress
                            </Text>
                          </div>
                        </Dragger>
                      </Col>
                    </Row>
                  )}

                  {uploadedPhotos.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '24px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                        borderRadius: '12px'
                      }}>
                        <div>
                          <Title level={5} style={{ margin: '0 0 4px', color: '#1f2937' }}>
                            Your Photos ({getVerifiedPhotosCount()}/{uploadedPhotos.length} verified)
                          </Title>
                          <Text style={{ color: '#6b7280' }}>
                            Compressed & verified by AI
                          </Text>
                        </div>
                        <Text style={{
                          background: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Click ⭐ to set primary photo
                        </Text>
                      </div>

                      <Row gutter={[16, 16]}>
                        {uploadedPhotos.map((photo) => {
                          const isVerifying = verifyingPhotos.has(photo.id);
                          const isVerified = photo.verified;
                          const hasResult = photo.verificationResult;

                          return (
                            <Col xs={12} sm={8} md={6} key={photo.id}>
                              <Card
                                size="small"
                                style={{
                                  position: 'relative',
                                  borderRadius: '16px',
                                  overflow: 'hidden',
                                  border: photo.isPrimary ? '3px solid #667eea' : isVerified ? '2px solid #10b981' : hasResult && !isVerified ? '2px solid #ef4444' : '1px solid #e5e7eb',
                                  boxShadow: photo.isPrimary ? '0 8px 25px rgba(102, 126, 234, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.3s ease'
                                }}
                                cover={
                                  <div style={{ position: 'relative', height: '180px', overflow: 'hidden' }}>
                                    <img
                                      src={photo.preview}
                                      alt={photo.name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        filter: isVerifying ? 'blur(2px)' : 'none',
                                        transition: 'filter 0.3s ease'
                                      }}
                                    />

                                    {isVerifying && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8))',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <div style={{ textAlign: 'center', color: 'white' }}>
                                          <Spin
                                            indicator={<LoadingOutlined style={{ fontSize: 32, color: 'white' }} spin />}
                                          />
                                          <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: '500' }}>
                                            AI Analyzing...
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {photo.isPrimary && (
                                      <Badge.Ribbon 
                                        text="Primary" 
                                        color="#667eea" 
                                        style={{ fontSize: '11px' }}
                                      />
                                    )}

                                    {/* Compression indicator */}
                                    {photo.compressed && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        left: '8px',
                                        background: 'rgba(59, 130, 246, 0.9)',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '10px',
                                        fontWeight: '600'
                                      }}>
                                        -{photo.compressionRatio}%
                                      </div>
                                    )}

                                    {!isVerifying && hasResult && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '12px',
                                        right: '12px',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: isVerified ? 
                                          'linear-gradient(135deg, #10b981, #059669)' : 
                                          'linear-gradient(135deg, #ef4444, #dc2626)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                                      }}>
                                        {isVerified ? (
                                          <CheckCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                                        ) : (
                                          <ExclamationCircleOutlined style={{ color: 'white', fontSize: '16px' }} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                }
                                actions={[
                                  <Tooltip title={photo.isPrimary ? "Primary Photo" : "Set as Primary"}>
                                    <Button
                                      type={photo.isPrimary ? "primary" : "text"}
                                      icon={photo.isPrimary ? <StarFilled /> : <StarOutlined />}
                                      onClick={() => setPrimaryPhoto(photo.id)}
                                      disabled={!isVerified}
                                      style={{
                                        border: 'none',
                                        background: photo.isPrimary ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent'
                                      }}
                                    />
                                  </Tooltip>,
                                  <Tooltip title="Remove Photo">
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => removePhoto(photo.id)}
                                      style={{ border: 'none' }}
                                    />
                                  </Tooltip>
                                ]}
                              >
                                {!isVerifying && hasResult && (
                                  <div style={{ padding: '8px 0' }}>
                                    {isVerified ? (
                                      <div>
                                        <Text style={{ 
                                          color: '#10b981', 
                                          fontSize: '13px', 
                                          fontWeight: '600',
                                          display: 'block',
                                          marginBottom: '4px'
                                        }}>
                                          ✓ Verified ({photo.verificationResult.confidence}%)
                                        </Text>
                                        <Text style={{ 
                                          color: '#2563eb', 
                                          fontSize: '11px',
                                          display: 'block',
                                          marginBottom: '2px'
                                        }}>
                                          Compressed: {photo.originalSizeKB}KB → {photo.compressedSizeKB}KB
                                        </Text>
                                        {photo.verificationResult.details.faceQuality && (
                                          <Text style={{ 
                                            color: '#6b7280', 
                                            fontSize: '11px',
                                            display: 'block'
                                          }}>
                                            Quality: {photo.verificationResult.details.faceQuality}
                                          </Text>
                                        )}
                                      </div>
                                    ) : (
                                      <Text style={{ 
                                        color: '#ef4444', 
                                        fontSize: '12px',
                                        lineHeight: '1.4'
                                      }}>
                                        {photo.verificationResult.details.message}
                                      </Text>
                                    )}
                                  </div>
                                )}
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  )}

                  <div>
                    {uploadedPhotos.length > 0 && (
                      <Alert
                        message={`${getVerifiedPhotosCount()} of ${uploadedPhotos.length} photos verified & compressed`}
                        description={
                          canProceedFromPhotos()
                            ? "Excellent! Your photos are compressed and verified. This will significantly reduce Firebase storage costs!"
                            : "Please upload at least one photo with a successfully detected face to continue."
                        }
                        type={canProceedFromPhotos() ? "success" : "warning"}
                        showIcon
                        style={{ 
                          marginBottom: '24px',
                          borderRadius: '12px',
                          border: 'none'
                        }}
                      />
                    )}

                    <Alert
                      message="Smart Photo Compression + AI Verification"
                      description="Photos are automatically compressed by ~80% using canvas optimization while maintaining quality. Then verified using Google's MediaPipe technology for the most accurate face detection available."
                      type="info"
                      showIcon
                      style={{
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #eff6ff, #dbeafe)'
                      }}
                    />

                    {canProceedFromPhotos() && (
                      <div style={{ textAlign: 'center', marginTop: '32px' }}>
                        <Button 
                          type="primary" 
                          size="large" 
                          onClick={nextStep}
                          style={{
                            height: '50px',
                            padding: '0 40px',
                            borderRadius: '25px',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: '600',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                          }}
                        >
                          Continue with {getVerifiedPhotosCount()} Compressed & Verified Photo{getVerifiedPhotosCount() !== 1 ? 's' : ''}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Steps 2, 3, 4 remain exactly the same as your original code... */}
            {/* Step 2: Basic Information */}
            {current === 1 && (
              <Card style={{
                borderRadius: '24px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  padding: '30px',
                  margin: '-24px -24px 32px',
                  borderRadius: '24px 24px 0 0',
                  textAlign: 'center'
                }}>
                  <UserOutlined style={{ fontSize: '36px', color: 'white', marginBottom: '12px' }} />
                  <Title level={3} style={{ color: 'white', margin: 0 }}>
                    Basic Information
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>First Name</span>}
                      name="firstName"
                      rules={[
                        { required: true, message: "Please enter your first name" },
                        { pattern: /^[a-zA-Z\s]+$/, message: "Only letters allowed" }
                      ]}
                    >
                      <Input 
                        placeholder="Enter your first name"
                        size="large"
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Last Name</span>}
                      name="lastName"
                      rules={[
                        { required: true, message: "Please enter your last name" },
                        { pattern: /^[a-zA-Z\s]+$/, message: "Only letters allowed" }
                      ]}
                    >
                      <Input 
                        placeholder="Enter your last name"
                        size="large"
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Gender</span>}
                      name="gender"
                      rules={[{ required: true, message: "Please select your gender" }]}
                    >
                      <Radio.Group style={{ width: '100%' }}>
                        <Radio.Button 
                          value="Male" 
                          style={{ 
                            borderRadius: '8px', 
                            marginRight: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          Male
                        </Radio.Button>
                        <Radio.Button 
                          value="Female"
                          style={{ 
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          Female
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Marital Status</span>}
                      name="maritalStatus"
                      rules={[{ required: true, message: "Please select your marital status" }]}
                    >
                      <Select
                        placeholder="Marital Status"
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={[
                          { value: "Never Married", label: "Never Married" },
                          { value: "Divorced", label: "Divorced" },
                          { value: "Widowed", label: "Widowed" },
                          { value: "Separated", label: "Separated" }
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Date of Birth</span>}
                      name="dateOfBirth"
                      rules={[
                        { required: true, message: "Please select your date of birth" },
                        {
                          validator(_, value) {
                            if (value && getAge(value) < 18) {
                              return Promise.reject(new Error("You must be 18 or older"));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <DatePicker
                        format={dateFormat}
                        size="large"
                        style={{ 
                          width: '100%',
                          borderRadius: '12px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Current Residence</span>}
                      name="currentResidence"
                      rules={[{ required: true, message: "Please enter your current residence" }]}
                    >
                      <Input 
                        placeholder="City, State"
                        size="large"
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Mother Tongue</span>}
                      name="motherTongue"
                      rules={[{ required: true, message: "Please select your mother tongue" }]}
                    >
                      <Select
                        placeholder="Select mother tongue"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.languages}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Height</span>}
                      name="height"
                      rules={[{ required: true, message: "Please select your height" }]}
                    >
                      <Select
                        placeholder="Height"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.height}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Education</span>}
                      name="education"
                      rules={[{ required: true, message: "Please select your education" }]}
                    >
                      <Select
                        placeholder="Education"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.education}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Occupation</span>}
                      name="occupation"
                      rules={[{ required: true, message: "Please select your occupation" }]}
                    >
                      <Select
                        placeholder="Occupation"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.occupation}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Flex justify="space-between" style={{ marginTop: '40px' }}>
                  <Button 
                    onClick={prevStep}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 24px',
                      borderRadius: '24px',
                      border: '2px solid #e5e7eb'
                    }}
                  >
                    Previous
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={nextStep}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 32px',
                      borderRadius: '24px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      fontWeight: '600'
                    }}
                  >
                    Next Step
                  </Button>
                </Flex>
              </Card>
            )}

            {/* Step 3: Religious Information */}
            {current === 2 && (
              <Card style={{
                borderRadius: '24px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  padding: '30px',
                  margin: '-24px -24px 32px',
                  borderRadius: '24px 24px 0 0',
                  textAlign: 'center'
                }}>
                  <BookOutlined style={{ fontSize: '36px', color: 'white', marginBottom: '12px' }} />
                  <Title level={3} style={{ color: 'white', margin: 0 }}>
                    Religious Information
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Sub Caste</span>}
                      name="subCaste"
                      rules={[{ required: true, message: "Please select your sub caste" }]}
                    >
                      <Select
                        placeholder="Select sub caste"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.subcaste}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Gothram</span>}
                      name="gothram"
                      rules={[{ required: true, message: "Please select your gothram" }]}
                    >
                      <Select
                        placeholder="Select gothram"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.gothram}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Nakshatram</span>}
                      name="nakshatram"
                      rules={[{ required: true, message: "Please select your nakshatram" }]}
                    >
                      <Select
                        placeholder="Birth star"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.nakshatram}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={4}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Padam</span>}
                      name="padam"
                      dependencies={['nakshatram']}
                      rules={[
                        ({ getFieldValue }) => ({
                          required: getPadam(getFieldValue('nakshatram')),
                          message: "Padam required"
                        })
                      ]}
                    >
                      <InputNumber
                        placeholder="Padam"
                        min={1}
                        max={4}
                        size="large"
                        style={{ 
                          width: '100%',
                          borderRadius: '12px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item 
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Raasi</span>}
                      name="raasi"
                    >
                      <Select
                        placeholder="Zodiac sign"
                        showSearch
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.raasis}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Place of Birth</span>}
                      name="placeOfBirth"
                      rules={[{ required: true, message: "Please enter place of birth" }]}
                    >
                      <Input 
                        placeholder="City, State"
                        size="large"
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Time of Birth</span>}
                      name="timeOfBirth"
                      rules={[{ required: true, message: "Please select time of birth" }]}
                    >
                      <TimePicker
                        use12Hours
                        format="h:mm A"
                        size="large"
                        style={{ 
                          width: '100%',
                          borderRadius: '12px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Dosham</span>}
                      name="dosham"
                      rules={[{ required: true, message: "Please select dosham status" }]}
                    >
                      <Radio.Group style={{ width: '100%' }}>
                        <Radio.Button 
                          value="Yes" 
                          style={{ 
                            borderRadius: '8px', 
                            marginRight: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          Yes
                        </Radio.Button>
                        <Radio.Button 
                          value="No"
                          style={{ 
                            borderRadius: '8px',
                            marginRight: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          No
                        </Radio.Button>
                        <Radio.Button 
                          value="Don't Know"
                          style={{ 
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          Don't Know
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Flex justify="space-between" style={{ marginTop: '40px' }}>
                  <Button 
                    onClick={prevStep}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 24px',
                      borderRadius: '24px',
                      border: '2px solid #e5e7eb'
                    }}
                  >
                    Previous
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={nextStep}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 32px',
                      borderRadius: '24px',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      border: 'none',
                      fontWeight: '600'
                    }}
                  >
                    Next Step
                  </Button>
                </Flex>
              </Card>
            )}

            {/* Step 4: About You */}
            {current === 3 && (
              <Card style={{
                borderRadius: '24px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #ec4899, #be185d)',
                  padding: '30px',
                  margin: '-24px -24px 32px',
                  borderRadius: '24px 24px 0 0',
                  textAlign: 'center'
                }}>
                  <HeartOutlined style={{ fontSize: '36px', color: 'white', marginBottom: '12px' }} />
                  <Title level={3} style={{ color: 'white', margin: 0 }}>
                    About Yourself
                  </Title>
                </div>

                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>About Yourself</span>}
                      name="aboutSelf"
                      rules={[
                        { required: true, message: "Please write about yourself" },
                        { min: 50, message: "Please write at least 50 characters" }
                      ]}
                    >
                      <TextArea
                        placeholder="Tell us about yourself, your interests, values, and what makes you unique..."
                        rows={4}
                        showCount
                        maxLength={500}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Partner Preferences</span>}
                      name="partnerPreferences"
                      rules={[
                        { required: true, message: "Please describe your partner preferences" },
                        { min: 30, message: "Please write at least 30 characters" }
                      ]}
                    >
                      <TextArea
                        placeholder="Describe your ideal partner, their qualities, background, and what you're looking for..."
                        rows={4}
                        showCount
                        maxLength={500}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: '600', color: '#374151', fontSize: '15px' }}>Family Type</span>}
                      name="familyType"
                      rules={[{ required: true, message: "Please select family type" }]}
                    >
                      <Select
                        placeholder="Family type"
                        size="large"
                        style={{ borderRadius: '12px' }}
                        options={jsonData.familyType}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {progress > 0 && (
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                    borderRadius: '12px',
                    marginTop: '24px'
                  }}>
                    <Text style={{ 
                      display: 'block', 
                      marginBottom: '12px', 
                      fontWeight: '600',
                      color: '#1e40af'
                    }}>
                      Creating your profile... {progress}%
                    </Text>
                    <Progress
                      percent={progress}
                      status="active"
                      strokeColor={{
                        '0%': '#667eea',
                        '100%': '#764ba2',
                      }}
                      style={{ marginBottom: '0' }}
                    />
                  </div>
                )}

                <Flex justify="space-between" style={{ marginTop: '40px' }}>
                  <Button 
                    onClick={prevStep} 
                    disabled={loading}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 24px',
                      borderRadius: '24px',
                      border: '2px solid #e5e7eb'
                    }}
                  >
                    Previous
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    size="large"
                    style={{
                      height: '48px',
                      padding: '0 32px',
                      borderRadius: '24px',
                      background: 'linear-gradient(135deg, #ec4899, #be185d)',
                      border: 'none',
                      fontWeight: '600',
                      boxShadow: '0 8px 25px rgba(236, 72, 153, 0.3)'
                    }}
                  >
                    {loading ? 'Creating Profile...' : 'Complete Profile'}
                  </Button>
                </Flex>
              </Card>
            )}
          </Form>
        </Spin>
      </div>
    </div>
  );
}